import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { LEAD_STAGES, STAGE_META, normStage, StageBadge } from "../components/leadStages";
import Contact360 from "../components/Contact360";
import { LeadQuotes } from "../components/LeadQuotes";
import { Modal } from "../components/ui";
import AcquistiContatto from "../components/AcquistiContatto";

const EURfmt = (n) => "€ " + Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });

const TL_ICON = { email_in: "📥", email_out: "📤", appuntamento: "📅", nota: "📝", stage: "🚩", funnel: "🎯" };
const TL_LABEL = { email_in: "Email ricevuta", email_out: "Email inviata", appuntamento: "Appuntamento", nota: "Nota", stage: "Fase", funnel: "Tappa funnel" };

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [events, setEvents] = useState([]);
  const [flow, setFlow] = useState(null);
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [saving, setSaving] = useState(false);
  const [revModal, setRevModal] = useState(null); // {one_shot, monthly}
  const [sentView, setSentView] = useState(null); // messaggio inviato aperto {loading, doc}

  const load = useCallback(() => {
    api.get(`/pipeline/${id}/timeline`).then((r) => {
      setLead(r.data.lead); setEvents(r.data.events || []); setFlow(r.data.flow || null);
      setNextAction(r.data.lead.next_action || "");
    }).catch(() => setLead(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const [ccPiano, setCcPiano] = useState(null);
  const [ccMsg, setCcMsg] = useState("");
  const isCC = lead && lead.source === "cambio-commercialista";
  useEffect(() => {
    if (isCC) api.get(`/cambio-commercialista/lead/${id}/piano`).then((r) => setCcPiano(r.data)).catch(() => {});
  }, [isCC, id]);
  const ccTag = async (tag) => {
    if ((tag === "non interessato" || tag === "preventivo scaduto")
        && !window.confirm(`Applicare «${tag}»? Ferma subito TUTTE le sequenze email in corso.`)) return;
    try {
      const r = await api.post(`/cambio-commercialista/lead/${id}/tag`, { tag });
      setCcMsg(r.data.nota || "Tag applicato");
      setCcPiano(r.data.piano || []);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  if (!lead) return <p className="muted">Caricamento…</p>;

  const stage = normStage(lead.stage);
  const closed = stage === "vinto" || stage === "perso";
  const curIdx = LEAD_STAGES.indexOf(stage);

  const stageDate = (s) => {
    const h = (lead.history || []).filter((x) => x.stage === s);
    return h.length ? h[0].at : null;
  };
  const norm = (iso) => String(iso).replace(/(\.\d{3})\d+/, "$1"); // microsecondi Python → Safari
  const fmt = (iso) => (iso ? new Date(norm(iso)).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit", timeZone: "Europe/Rome" }) : "");
  const fmtFull = (iso) => (iso ? new Date(norm(iso)).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Rome" }) : "");
  const itDate = (iso) => { if (!iso) return "—"; const p = String(iso).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };

  const setStage = async (s) => {
    try { await api.put(`/pipeline/${id}`, { stage: s }); load(); }
    catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const saveNext = async () => {
    setSaving(true);
    try { await api.put(`/pipeline/${id}`, { next_action: nextAction }); load(); }
    catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/pipeline/${id}/activity`, { text: note.trim() }); setNote(""); load(); }
    catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const setOutcome = async (outcome) => {
    const msg = outcome === "cliente"
      ? "Confermi? L'anagrafica collegata diventa CLIENTE e si fermano le email del funnel lead."
      : "Confermi? Il percorso si chiude ma continuerà a ricevere le email dei lead.";
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/pipeline/${id}/outcome`, { outcome });
      if (outcome === "cliente") {
        alert(`Fatto! Anagrafiche aggiornate a cliente: ${r.data.updated_contacts}`);
        setRevModal({ one_shot: lead.one_shot_revenue || "", monthly: lead.monthly_revenue || "" });
      }
      load();
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const saveRevenue = async () => {
    try {
      await api.put(`/pipeline/${id}`, {
        one_shot_revenue: Number(revModal.one_shot) || 0,
        monthly_revenue: Number(revModal.monthly) || 0,
      });
      setRevModal(null); load();
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const openSent = async (e) => {
    setSentView({ loading: true });
    try { const r = await api.get(`/emails/sent/${e.sid}`); setSentView({ doc: r.data }); }
    catch { setSentView(null); alert("Messaggio non trovato"); }
  };

  const detach = async (kind, value) => {
    if (!window.confirm(`Scollegare "${value}" da questo lead?`)) return;
    try { await api.post(`/pipeline/${id}/detach-contact`, { [kind]: value }); load(); }
    catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const deleteLead = async () => {
    if (!window.confirm(`Eliminare definitivamente il lead "${lead.name || lead.email || lead.phone}"?\nLa cronologia del funnel andrà persa (email e anagrafiche NON vengono toccate).`)) return;
    try { await api.delete(`/pipeline/${id}`); navigate("/lead"); }
    catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div data-testid="lead-detail-page">
      <p style={{ display: "flex", alignItems: "center" }}>
        <Link to="/lead">← Tutti i lead</Link>
        <button className="btn xs danger" style={{ marginLeft: "auto" }} onClick={deleteLead} data-testid="lead-delete-btn">🗑 Elimina lead</button>
      </p>
      <h1 className="page-title">{lead.name || lead.email || lead.phone}</h1>
      <p className="page-sub">
        {lead.email || "(nessuna email)"}
        <button className="btn xs" style={{ marginLeft: 6 }} data-testid="lead-edit-email-btn"
          onClick={async () => {
            const em = window.prompt("Email del lead (per agganciare tutte le sue email alla cronologia):", lead.email || "");
            if (em === null) return;
            try { await api.put(`/pipeline/${id}`, { email: em.trim() }); load(); }
            catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
          }}>{lead.email ? "✎ email" : "＋ email"}</button>
        {lead.phone ? ` · ${lead.phone}` : ""}
        <button className="btn xs" style={{ marginLeft: 8 }} data-testid="lead-edit-phone-btn"
          onClick={async () => {
            const p = window.prompt("Telefono del lead (per agganciare anche i messaggi WhatsApp):", lead.phone || "");
            if (p === null) return;
            try { await api.put(`/pipeline/${id}`, { phone: p.trim() }); load(); }
            catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
          }}>{lead.phone ? "✎ tel" : "＋ telefono"}</button>
        {" "}· <StageBadge stage={lead.stage} />
        {lead.source && <span className="muted"> · fonte: {lead.source}</span>}
        {lead.created_at && <span className="muted"> · dal {fmt(lead.created_at)}</span>}
      </p>
      {flow && flow.flusso && (
        <div className="panel" data-testid="lead-flow-now" style={{ padding: "10px 14px", marginBottom: 10 }}>
          <strong>📍 {flow.flusso}</strong>
          {flow.prossima_email && (
            <span> · Prossima email: «{flow.prossima_email.oggetto}» — <strong>{itDate(flow.prossima_email.quando)}</strong>
              {flow.prossima_email.nota ? <span className="muted" style={{ fontSize: 12 }}> ({flow.prossima_email.nota})</span> : null}
            </span>
          )}
        </div>
      )}

      {isCC && (
        <div className="panel" data-testid="lead-cc-panel" style={{ padding: "12px 14px", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <strong>🔁 Funnel cambio commercialista</strong>
            {(lead.tag_names || []).map((t) => <span key={t} className="pill" data-testid={`lead-cc-tag-${t}`}>{t}</span>)}
            <div className="spacer" />
            <button className="btn sm" onClick={() => ccTag("preventivo inviato")} data-testid="cc-tag-preventivo">💶 Preventivo inviato</button>
            <button className="btn sm" onClick={() => ccTag("riprendere")} data-testid="cc-tag-riprendere">⏳ Riprendere</button>
            <button className="btn sm danger" onClick={() => ccTag("non interessato")} data-testid="cc-tag-non-interessato">🚫 Non interessato</button>
            <button className="btn sm danger" onClick={() => ccTag("preventivo scaduto")} data-testid="cc-tag-scaduto">⌛ Preventivo scaduto</button>
          </div>
          {ccMsg && <p className="muted" style={{ margin: "6px 0 0", fontSize: 12.5 }} data-testid="cc-tag-msg">{ccMsg}</p>}
          {ccPiano && ccPiano.length > 0 && (
            <table style={{ marginTop: 10, fontSize: 12.5 }} data-testid="cc-piano-table">
              <thead><tr><th>Sequenza</th><th>Step</th><th>Tipo</th><th>Data</th><th>Stato</th></tr></thead>
              <tbody>
                {ccPiano.map((r, i) => (
                  <tr key={i}>
                    <td>{r.sequenza}</td><td>{r.step}</td><td>{r.tipo}</td>
                    <td>{itDate(r.data)}</td>
                    <td>{r.stato.startsWith("inviato") ? "✅ " + r.stato : r.stato.startsWith("fermato") ? "⛔ " + r.stato : "🕐 " + r.stato}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {((lead.extra_emails || []).length > 0 || (lead.extra_phones || []).length > 0) && (
        <p className="page-sub" data-testid="lead-extra-contacts" style={{ marginTop: -6 }}>
          <span className="muted">Altri recapiti collegati: </span>
          {(lead.extra_emails || []).map((em) => (
            <span key={em} className="pill" style={{ marginRight: 6 }}>✉️ {em}
              <button className="btn xs" style={{ marginLeft: 4, padding: "0 4px" }} title="Scollega" onClick={() => detach("email", em)} data-testid={`lead-detach-email-${em}`}>✕</button>
            </span>
          ))}
          {(lead.extra_phones || []).map((ph) => (
            <span key={ph} className="pill" style={{ marginRight: 6 }}>📱 {ph}
              <button className="btn xs" style={{ marginLeft: 4, padding: "0 4px" }} title="Scollega" onClick={() => detach("phone", ph)} data-testid={`lead-detach-phone-${ph}`}>✕</button>
            </span>
          ))}
        </p>
      )}

      {closed && (
        <div className={"panel lead-outcome " + (stage === "vinto" ? "won" : "lost")} data-testid="lead-outcome-banner">
          <strong>{stage === "vinto" ? "🎉 È diventato cliente" : "Percorso chiuso · non ha accettato (resta lead)"}</strong>
          {lead.closed_at && <span className="muted"> · {fmt(lead.closed_at)}</span>}
          {stage === "vinto" && (
            <span style={{ marginLeft: 12 }} data-testid="lead-revenue-info">
              {(lead.one_shot_revenue || lead.monthly_revenue)
                ? <>💶 {lead.one_shot_revenue ? `${EURfmt(lead.one_shot_revenue)} una tantum` : ""}{lead.one_shot_revenue && lead.monthly_revenue ? " + " : ""}{lead.monthly_revenue ? `${EURfmt(lead.monthly_revenue)}/mese` : ""}</>
                : <span className="muted">importi non indicati</span>}
              <button className="btn xs" style={{ marginLeft: 8 }} onClick={() => setRevModal({ one_shot: lead.one_shot_revenue || "", monthly: lead.monthly_revenue || "" })} data-testid="lead-revenue-edit-btn">💶 Importi</button>
            </span>
          )}
          <button className="btn sm" style={{ marginLeft: 12 }} onClick={() => setStage("primo_contatto")} data-testid="lead-reopen-btn">Riapri percorso</button>
        </div>
      )}

      {revModal && (
        <Modal title="💶 Fatturato previsto dal nuovo cliente" onClose={() => setRevModal(null)}>
          <p className="muted" style={{ fontSize: 13 }}>Questi importi aggiornano subito il 🎯 Obiettivo annuo (Dashboard e Margini clienti). Il canone conta per i mesi rimanenti dell'anno; quando il cliente inizierà a fatturare davvero, subentreranno i dati reali.</p>
          <div className="field"><label>Compenso una tantum € (es. pratica di apertura)</label>
            <input type="number" min="0" value={revModal.one_shot} onChange={(e) => setRevModal({ ...revModal, one_shot: e.target.value })} data-testid="lead-oneshot-input" />
          </div>
          <div className="field"><label>Canone mensile ricorrente €</label>
            <input type="number" min="0" value={revModal.monthly} onChange={(e) => setRevModal({ ...revModal, monthly: e.target.value })} data-testid="lead-monthly-input" />
          </div>
          <div className="toolbar"><div className="spacer" />
            <button className="btn" onClick={() => setRevModal(null)}>Annulla</button>
            <button className="btn primary" onClick={saveRevenue} data-testid="lead-revenue-save">Salva</button>
          </div>
        </Modal>
      )}

      <Contact360 email={lead.email} phone={lead.phone} name={lead.name} leadId={id} hide={["lead", "emails"]} />
      <AcquistiContatto email={lead.email} />

      {(lead.segmento || lead.data_checkup || (lead.esito_leve || []).length > 0) && (
        <div className="panel" data-testid="lead-checkup-panel">
          <h3>🩺 Check-up fiscale</h3>
          <div className="dash-cards" style={{ marginTop: 6 }}>
            <div className="dash-card"><div className="num" style={{ fontSize: 18 }} data-testid="ck-segmento">{lead.segmento || "—"}</div><div className="lbl">Segmento</div></div>
            <div className="dash-card"><div className="num" style={{ fontSize: 18 }} data-testid="ck-data">{itDate(lead.data_checkup)}</div><div className="lbl">Compilato il</div></div>
            <div className="dash-card"><div className="num" style={{ fontSize: 18 }} data-testid="ck-leve">{lead.n_leve ?? (lead.esito_leve || []).length}</div><div className="lbl">Leve individuate</div></div>
            <div className="dash-card highlight"><div className="num" style={{ fontSize: 18 }} data-testid="ck-scadenza">{itDate(lead.prossima_scadenza)}</div><div className="lbl">Prossima scadenza</div></div>
          </div>
          {lead.leva_prossima_scadenza && (
            <p style={{ marginBottom: 4 }} data-testid="ck-leva">⏳ <strong>{lead.leva_prossima_scadenza.titolo}</strong>
              {lead.leva_prossima_scadenza.importo != null ? ` · fino a ${EURfmt(lead.leva_prossima_scadenza.importo)}` : " · da quantificare"}
              {lead.leva_prossima_scadenza.riferimento ? ` · ${lead.leva_prossima_scadenza.riferimento}` : ""}</p>
          )}
          <p className="muted" style={{ fontSize: 12, margin: 0 }} data-testid="ck-provenienza">
            Provenienza: {lead.provenienza || "—"}{lead.stato_funnel ? ` · stato funnel: ${lead.stato_funnel}` : ""}
            {lead.newsletter_entry_at ? ` · newsletter dal ${itDate(lead.newsletter_entry_at)}` : " · niente newsletter (consenso marketing non dato)"}
          </p>
        </div>
      )}

      <div className="panel">
        <h3>Percorso</h3>
        <div className="lead-steps" data-testid="lead-steps">
          {LEAD_STAGES.map((s, i) => {
            const done = closed ? Boolean(stageDate(s)) : i < curIdx;
            const current = !closed && i === curIdx;
            const d = stageDate(s);
            return (
              <div key={s} className={"lead-step" + (done ? " done" : "") + (current ? " current" : "")}
                onClick={() => !closed && setStage(s)} data-testid={`lead-step-${s}`}
                style={{ cursor: closed ? "default" : "pointer" }} title={closed ? "" : "Clicca per portare il lead a questa fase"}>
                {done ? "✓ " : ""}{STAGE_META[s].label}
                <span className="st-date">{d ? fmt(d) : "\u00a0"}</span>
              </div>
            );
          })}
        </div>
        {!closed && (
          <div className="toolbar" style={{ marginTop: 10 }}>
            {curIdx < LEAD_STAGES.length - 1 && (
              <button className="btn primary" onClick={() => setStage(LEAD_STAGES[curIdx + 1])} data-testid="lead-advance-btn">
                Avanti → {STAGE_META[LEAD_STAGES[curIdx + 1]].label}
              </button>
            )}
            <div className="spacer" />
            <button className="btn" style={{ borderColor: "#22a35a", color: "#22a35a" }} onClick={() => setOutcome("cliente")} data-testid="lead-won-btn">🎉 È diventato cliente</button>
            <button className="btn" style={{ borderColor: "#c46b66", color: "#c46b66" }} onClick={() => setOutcome("rimane_lead")} data-testid="lead-lost-btn">Non ha accettato · resta lead</button>
          </div>
        )}
      </div>

      <div className="grid-2" style={{ alignItems: "start" }}>
        <div className="panel">
          <h3>Prossimi passi</h3>
          <textarea rows={3} value={nextAction} onChange={(e) => setNextAction(e.target.value)}
            placeholder="es. Richiamarlo giovedì per il preventivo…" data-testid="lead-next-action" />
          <div className="modal-actions" style={{ marginTop: 8 }}>
            <button className="btn primary sm" onClick={saveNext} disabled={saving} data-testid="lead-next-action-save">Salva</button>
          </div>
        </div>
        <div className="panel">
          <h3>Aggiungi nota</h3>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="es. Appuntamento andato bene, interessato al forfettario…" data-testid="lead-note-input" />
          <div className="modal-actions" style={{ marginTop: 8 }}>
            <button className="btn primary sm" onClick={addNote} disabled={!note.trim()} data-testid="lead-note-save">Aggiungi</button>
          </div>
        </div>
      </div>

      <LeadQuotes personId={id} leadEmail={lead.email || ""} leadName={lead.name || ""} />

      <div className="panel" data-testid="lead-timeline-panel">
        <h3>Cronologia completa ({events.length})</h3>
        {events.length === 0 ? <p className="empty">Ancora nessun evento.</p> : (
          <ul className="tl">
            {events.map((e, i) => (
              <li key={i} data-testid={`tl-event-${i}`}>
                <span className="tl-ico" title={TL_LABEL[e.type]}>{TL_ICON[e.type] || "•"}</span>
                <span>
                  <span className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{TL_LABEL[e.type]}{e.status ? ` · ${e.status}` : ""}</span>
                  <br />
                  {e.type === "email_in" && e.email_id
                    ? <Link to={`/email/${e.email_id}`} state={{ backTo: `/lead/${id}`, backLabel: "Scheda lead" }}>{e.title}</Link>
                    : e.type === "email_out" && e.sid
                    ? <a href="#apri" onClick={(ev) => { ev.preventDefault(); openSent(e); }} data-testid={`tl-open-sent-${i}`}>{e.title}</a>
                    : e.title}
                </span>
                <span className="tl-at">{fmtFull(e.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {sentView && (
        <Modal title="📤 Messaggio inviato dallo Studio" onClose={() => setSentView(null)}>
          {sentView.loading ? <p className="muted">Caricamento…</p> : (
            <div data-testid="sent-view-modal">
              <p style={{ marginTop: 0 }}>
                <strong>{sentView.doc.subject || "(senza oggetto)"}</strong><br />
                <span className="muted" style={{ fontSize: 13 }}>
                  A: {sentView.doc.to} · {fmtFull(sentView.doc.sent_at)}
                  {sentView.doc.channel && sentView.doc.channel !== "email" ? ` · ${sentView.doc.channel === "whatsapp" ? "WhatsApp" : sentView.doc.channel.toUpperCase()}` : ""}
                </span>
              </p>
              <div style={{ maxHeight: "55vh", overflowY: "auto", background: "#fff", color: "#111", borderRadius: 8, padding: 14, fontSize: 14 }}>
                {sentView.doc.body_html
                  ? <div dangerouslySetInnerHTML={{ __html: sentView.doc.body_html.replace(/src="cid:(news\d+)"/g, 'src="/api/drip/asset/$1.png"') }} />
                  : <div style={{ whiteSpace: "pre-wrap" }}>{sentView.doc.body_text || sentView.doc.subject || "(nessun contenuto)"}</div>}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
